"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  ClipboardList,
  GraduationCap,
  Landmark,
  MapPin,
  Megaphone,
  Route,
  School,
  Search,
  ShieldCheck,
  TrendingDown,
  Users,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Breadcrumbs from "@/components/seo/Breadcrumbs";
import type { PublicStats } from "@/lib/api/content";
import { FAQ_ITEMS } from "./faq-data";

const REGISTER_HREF = "/register?role=institute";

// ─── Copy blocks ─────────────────────────────────────────────────────────────

const INSTITUTE_FRICTIONS: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Empty seats in scheduled batches",
    body: "A simulator batch runs whether twelve seats fill or five do. The instructor is booked, the equipment is reserved, the classroom is paid for — the only variable is enrolment, and it is the variable you control least. Every underfilled batch is fixed cost against fee income that never arrived.",
    Icon: TrendingDown,
  },
  {
    title: "Marketing spend that reaches the wrong audience",
    body: "Generic digital advertising puts your STCW refresher in front of people who will never hold a seafarer's document. Even maritime-audience channels mostly reach professionals at the wrong moment: not when they need the certificate, but whenever the algorithm finds them. You pay for impressions; you need enrolments.",
    Icon: Megaphone,
  },
  {
    title: "No signal on what the market actually needs",
    body: "Should you schedule another tanker familiarization batch, or invest in the new management course? Today that decision runs on instinct, last year's enrolments and agent chatter. Nobody hands an institute measured demand — which skills maritime professionals are actually missing, in what numbers, right now.",
    Icon: Search,
  },
  {
    title: "Directories that generate noise, not students",
    body: "Most listing sites sell your contact details into a lead marketplace. The enquiries that arrive are half price-shopping bots and agent spam, your listing sits beside unverified competitors, and the platform's incentive is volume — yours is filled seats.",
    Icon: TrendingDown,
  },
];

const DEMAND_CHAIN: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "A professional sets a career target",
    body: "A chief officer targeting marine superintendent. A second engineer targeting technical superintendence. A crewing executive targeting fleet manager. They pick their target roles in the platform's career-intelligence engine, and the engine goes to work on their actual profile.",
  },
  {
    step: "02",
    title: "The engine names their open skill gaps",
    body: "Their rank, sea service, certificates and existing skills are assessed against the target role's qualification rules — hundreds of shore positions, thousands of skill-gap rules, kept current. The output is not vague advice: it is a named list of what they already transfer and exactly what they are missing.",
  },
  {
    step: "03",
    title: "Gaps map to courses — your courses",
    body: "Every skill-gap rule is wired to the canonical courses that close it, scored for relevance and weighted by rank and career archetype. When the gap is named, the platform's recommendation attaches the published offerings of approved institutes: course title, institute name, fee. That attachment is your listing, arriving at the precise moment of identified need.",
  },
  {
    step: "04",
    title: "The enquiry comes to you",
    body: "The professional sees your offering with its batches — dates, deadlines, available seats — and sends a course enquiry. It lands in your institute dashboard with the course context attached and triggers a notification. Your admissions team takes it from there, through your own enrolment process, on your own terms.",
  },
];

const DISCOVERY_POINTS: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Location-first search",
    body: "Professionals filter the directory by country, state and city — because a course you must attend in person is a course you must travel to. Your institute's taxonomy-resolved location places you in front of the professionals who can actually reach your classrooms, in your port city and beyond it.",
    Icon: MapPin,
  },
  {
    title: "Credibility markers up front",
    body: "Accreditation details and approval status are part of your public profile — including flags like DG Shipping approval where relevant. In a market where professionals have learned to be wary of certificate mills, verified institute status is not decoration; it is the shortlist filter.",
    Icon: BadgeCheck,
  },
  {
    title: "Searchable by name and course",
    body: "Professionals who already know your name find you directly; professionals who know only the certificate they need find you through the course. Both paths lead to the same place: your offerings, your batches, your enquiry channel.",
    Icon: Search,
  },
];

const LISTING_STEPS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "Register in one step",
    body: "A single registration form — institute name, registration number, location, accreditation details, contacts — creates your institute profile, company account and owner login immediately. No multi-week onboarding programme, no implementation project. Most institutes finish in under fifteen minutes.",
  },
  {
    step: "02",
    title: "Pass the approval review",
    body: "Our team reviews every institute before anything can be published — the only gate in the process. It exists so that professionals can trust the directory, which is precisely what makes your listing worth having. Have your accreditation details ready and it is a formality.",
  },
  {
    step: "03",
    title: "Pick your courses from the catalogue",
    body: "Listing a course means selecting it from the canonical course picker — STCW certificates, flag endorsements, simulator training, professional development — then adding your specifics: fee, duration, delivery mode, description. You are claiming and detailing existing entries, not authoring a catalogue. Because you pick the canonical entry, your offering is wired into the skill-gap recommendation graph automatically.",
  },
  {
    step: "04",
    title: "Schedule batches, take enquiries",
    body: "Add batch dates, registration deadlines, capacity and any batch-level fee. Open batches with seats surface to professionals planning training; enquiries arrive in your dashboard as structured leads with notifications. Invite colleagues as staff with granular permissions — course listing, batch management, lead handling are separately assignable.",
  },
];

const INSTITUTE_TYPES: { name: string; body: string; Icon: LucideIcon }[] = [
  {
    name: "STCW & statutory course providers",
    body: "Your courses are mandatory, recurring and expiry-driven — the steadiest demand in maritime training, and the easiest to reach badly. Professionals track certificate expiry inside CrewAhead's document vault and plan refreshers against joining dates. Listing puts your refresher batches in front of professionals whose documents are approaching expiry, in the location they can actually travel to.",
    Icon: ShieldCheck,
  },
  {
    name: "Maritime academies & pre-sea institutions",
    body: "Pre-sea programmes sell a career, not a certificate — and the career question is exactly what this platform answers. Cadets and their families evaluating your academy are also asking what your graduates become; the platform's career mapping is the context in which that question gets a concrete answer. Listing also opens the faculty channel: academies recruit instructors from the same professional base.",
    Icon: School,
  },
  {
    name: "Specialist & simulator training centres",
    body: "DP, ECDIS, liquid cargo handling, high voltage, GMDSS — specialist courses have thin, scattered demand that is expensive to reach through advertising and perfect for demand matching. When a professional's profile says their next rank or vessel type requires your specialty, your course appears with it. Your simulator time is also listable as a resource for other institutes between your own batches.",
    Icon: Wrench,
  },
  {
    name: "Professional-development & management trainers",
    body: "Sea-to-shore transitions are the platform's home ground, and they run on exactly your catalogue: management, commercial, compliance and technical upskilling for officers moving ashore. The skill-gap engine was built to identify this demand by name — ISM auditing, fleet budgeting, terminal systems — and there is no other channel where your management course is recommended because a named gap demanded it.",
    Icon: GraduationCap,
  },
];

const BEYOND_COURSES: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Monetise idle capacity",
    body: "A bridge simulator free on Thursdays. A classroom dark between batches. List spare facilities as resources — other institutes with demand but no site can enquire, and idle infrastructure becomes revenue instead of overhead.",
    Icon: Wrench,
  },
  {
    title: "Recruit specialist faculty",
    body: "Need a visiting LNG instructor for one batch, or a permanent senior assessor? Institutes can post faculty and trainer job listings to the same maritime professional base that uses the platform for careers — people with the exact sea-time and certification background your courses require.",
    Icon: Users,
  },
  {
    title: "Refer and be referred",
    body: "See what other institutes are running. Refer your students to courses you don't offer, and take referrals for the ones you do. The training market works better as a network than as a set of guarded silos — and the platform makes the network visible.",
    Icon: School,
  },
];

// ─── Motion helpers ──────────────────────────────────────────────────────────

function useInViewOnce(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options, inView]);

  return [ref, inView] as const;
}

function useCountUp(target: number, inView: boolean, reduced: boolean) {
  const [value, setValue] = useState(reduced ? target : 0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    if (!inView || startedRef.current) return;
    startedRef.current = true;
    let start: number | undefined;
    const duration = 1400;
    let raf: number;

    const step = (ts: number) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, inView, reduced]);

  return value;
}

function LiveStat({ value, label }: { value: number; label: string }) {
  const [ref, inView] = useInViewOnce({ threshold: 0.3, rootMargin: "-60px" });
  const reduced = useReducedMotion();
  const display = useCountUp(value, inView, !!reduced);

  return (
    <div ref={ref} className="flex flex-col">
      <span className="font-display text-3xl font-bold text-white sm:text-4xl">
        {display.toLocaleString("en-US")}
      </span>
      <span className="mt-1 text-sm text-ash">{label}</span>
    </div>
  );
}

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Hero animated element: the demand link ──────────────────────────────────
// Three nodes — an identified skill gap, the canonical course that closes it,
// and the institute's batch — connected by lines that draw themselves in
// sequence, with a pulse travelling the full path. Static under reduced motion.

function DemandLink() {
  const [ref, inView] = useInViewOnce({ threshold: 0.3, rootMargin: "-60px" });
  const reduced = useReducedMotion();
  const active = inView || !!reduced;

  const linkProps = (delay: number) => ({
    initial: { pathLength: reduced ? 1 : 0, opacity: reduced ? 1 : 0 },
    animate: active ? { pathLength: 1, opacity: 1 } : undefined,
    transition: { duration: reduced ? 0 : 0.7, delay: reduced ? 0 : delay, ease: "easeInOut" as const },
  });

  const nodeProps = (delay: number) => ({
    initial: reduced ? { opacity: 1 } : { opacity: 0, y: 10 },
    animate: active ? { opacity: 1, y: 0 } : undefined,
    transition: { duration: reduced ? 0 : 0.45, delay: reduced ? 0 : delay },
  });

  return (
    <div ref={ref} className="relative rounded-[2rem] border border-neon-cyan/15 bg-panel/70 p-4 shadow-2xl shadow-neon-cyan/10 backdrop-blur">
      <div className="rounded-[1.5rem] border border-neon-cyan/10 bg-void p-5">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon-cyan">
            Demand link · how a gap becomes your enrolment
          </p>
          <p className="hidden font-mono text-[11px] text-ash sm:block">ENGINE → COURSE → BATCH</p>
        </div>

        <svg
          viewBox="0 0 560 400"
          role="img"
          aria-label="A diagram connecting an identified skill gap to a canonical course and then to an institute's scheduled batch, illustrating demand-matched course discovery."
          className="mt-4 h-auto w-full"
        >
          {/* connection lines */}
          <motion.path
            d="M 155 105 C 210 105, 240 190, 295 190"
            fill="none"
            stroke="#a855f7"
            strokeWidth="1.8"
            strokeDasharray="5 5"
            {...linkProps(0.5)}
          />
          <motion.path
            d="M 405 205 C 440 220, 450 270, 455 290"
            fill="none"
            stroke="#00ff9d"
            strokeWidth="1.8"
            strokeDasharray="5 5"
            {...linkProps(1.1)}
          />

          {/* travelling pulse */}
          {!reduced && active && (
            <circle r="3.5" fill="#00f3ff">
              <animateMotion
                dur="3.5s"
                repeatCount="indefinite"
                path="M 155 105 C 210 105, 240 190, 295 190 L 350 198 C 400 210, 440 260, 455 290"
              />
            </circle>
          )}

          {/* node 1: skill gap */}
          <motion.g {...nodeProps(0)}>
            <rect x="20" y="62" width="135" height="86" rx="12" fill="#121420" stroke="#ff007f" strokeOpacity="0.5" />
            <text x="34" y="88" fill="#ff007f" fontSize="9.5" fontWeight="700" fontFamily="var(--font-mono)">
              IDENTIFIED GAP
            </text>
            <text x="34" y="106" fill="#e5e7ef" fontSize="11.5" fontWeight="600">
              ISM Auditing
            </text>
            <text x="34" y="122" fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
              C/O → MARINE SUPT
            </text>
            <text x="34" y="136" fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
              READINESS 71%
            </text>
          </motion.g>

          {/* node 2: canonical course */}
          <motion.g {...nodeProps(0.7)}>
            <rect x="295" y="150" width="120" height="86" rx="12" fill="#121420" stroke="#a855f7" strokeOpacity="0.55" />
            <text x="309" y="176" fill="#a855f7" fontSize="9.5" fontWeight="700" fontFamily="var(--font-mono)">
              COURSE MATCH
            </text>
            <text x="309" y="194" fill="#e5e7ef" fontSize="11.5" fontWeight="600">
              ISM Internal
            </text>
            <text x="309" y="208" fill="#e5e7ef" fontSize="11.5" fontWeight="600">
              Auditor
            </text>
            <text x="309" y="224" fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
              RELEVANCE 92/100
            </text>
          </motion.g>

          {/* node 3: institute batch */}
          <motion.g {...nodeProps(1.3)}>
            <rect x="398" y="288" width="142" height="92" rx="12" fill="#121420" stroke="#00ff9d" strokeOpacity="0.55" />
            <text x="412" y="314" fill="#00ff9d" fontSize="9.5" fontWeight="700" fontFamily="var(--font-mono)">
              YOUR INSTITUTE
            </text>
            <text x="412" y="332" fill="#e5e7ef" fontSize="11.5" fontWeight="600">
              Batch · 14 Sep
            </text>
            <text x="412" y="348" fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
              6 SEATS OPEN
            </text>
            <text x="412" y="362" fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
              REG CLOSES 07 SEP
            </text>
          </motion.g>
        </svg>

        <div className="mt-3 grid gap-2 border-t border-white/10 pt-3 font-mono text-[11px] text-ash sm:grid-cols-3">
          <p>
            <span className="text-neon-magenta">GAP</span> measured, not guessed
          </p>
          <p>
            <span className="text-neon-purple">MATCH</span> taxonomy-wired
          </p>
          <p>
            <span className="text-neon-green">LEAD</span> direct to admissions
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── CTA blocks ──────────────────────────────────────────────────────────────

function PrimaryCta({ large = false }: { large?: boolean }) {
  return (
    <Link
      href={REGISTER_HREF}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan font-bold text-obsidian shadow-glow-cyan transition hover:scale-[1.01] hover:bg-neon-cyan/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian ${
        large ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm"
      }`}
    >
      <GraduationCap className={large ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      List your institute
    </Link>
  );
}

function SignInLink({ className = "" }: { className?: string }) {
  return (
    <p className={`text-sm text-ash ${className}`}>
      Already have an account?{" "}
      <Link
        href="/login"
        className="font-semibold text-neon-cyan transition-colors hover:text-neon-cyan/80"
      >
        Sign in
      </Link>
    </p>
  );
}

function MidPageCta() {
  return (
    <Reveal className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-5 rounded-[2rem] border border-neon-green/15 bg-panel p-8 text-center md:flex-row md:justify-between md:text-left">
        <div>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold text-neon-green md:justify-start">
            <CalendarCheck className="h-4 w-4" aria-hidden="true" />
            No listing fee. No per-lead charge. Enquiries go to you.
          </p>
          <p className="mt-2 text-lg font-bold text-white">
            Fifteen minutes of registration, one approval review — and your
            courses stand in front of measured demand.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 md:items-end">
          <PrimaryCta />
          <SignInLink />
        </div>
      </div>
    </Reveal>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ListYourInstituteContent({ stats }: { stats: PublicStats | null }) {
  const prefersReducedMotion = useReducedMotion();

  const heroFade = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 18 },
    visible: { opacity: 1, y: 0 },
  };

  const demandStats = stats
    ? (
        [
          { key: "seafarers", label: "seafarers building careers", value: stats.seafarers || stats.activeSeafarers },
          { key: "shoreProfessionals", label: "shore professionals", value: stats.shoreProfessionals || stats.activeShoreProfessionals },
          { key: "shorePositionsWithGuidance", label: "roles with skill-gap guidance", value: stats.shorePositionsWithGuidance },
          { key: "skillGapRules", label: "skill-gap rules driving demand", value: stats.skillGapRules },
          { key: "courses", label: "courses in the taxonomy", value: stats.courses },
          { key: "availableSeats", label: "open batch seats listed", value: stats.availableSeats },
        ] as const
      ).filter((item) => item.value > 0)
    : [];

  return (
    <main className="min-h-screen overflow-hidden bg-obsidian text-foreground">
      <Breadcrumbs />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative px-4 pb-16 pt-10 sm:px-6 sm:pt-14 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(0,243,255,0.12),transparent_32%),radial-gradient(circle_at_85%_25%,rgba(0,255,157,0.08),transparent_34%)]"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neon-green/20 bg-neon-green/8 px-4 py-2 text-sm font-semibold text-neon-green">
              <Route className="h-4 w-4" aria-hidden="true" />
              Demand-matched course discovery · not another directory
            </div>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              Your courses, shown to seafarers who already know what
              they&apos;re missing.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ash">
              CrewAhead&apos;s career-intelligence engine tells maritime
              professionals exactly which skills stand between them and their
              target role — by name, with evidence. Then it points them at the
              courses that close each gap. Approved institutes with published
              offerings appear at that precise moment: course, institute, fee,
              batch dates, open seats. This is not a listing site hoping for
              traffic. It is measured demand, delivered to your admissions
              desk.
            </p>
            <div className="mt-9 flex flex-col gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryCta large />
                <Link
                  href="/institutes"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-neon-purple/30 bg-neon-purple/8 px-7 py-4 font-bold text-neon-purple transition hover:border-neon-purple/60"
                >
                  <School className="h-5 w-5" aria-hidden="true" />
                  See the live directory
                </Link>
              </div>
              <SignInLink />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <DemandLink />
          </motion.div>
        </div>
      </section>

      {/* ── Live demand stats ────────────────────────────────────────────── */}
      {demandStats.length > 0 && (
        <section
          aria-label="Live platform demand statistics"
          className="border-y border-white/5 bg-panel/30 px-4 py-12 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-neon-green">
              The demand side, as it stands today — live counts
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {demandStats.map((item) => (
                <LiveStat key={item.key} value={item.value} label={item.label} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── The institute's problem ──────────────────────────────────────── */}
      <section aria-labelledby="friction-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-magenta">
              The enrollment problem
            </p>
            <h2 id="friction-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Good courses run underfilled. That is a distribution failure,
              not a quality one.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Maritime training is a high-fixed-cost business: simulators,
              instructors, classrooms and accreditation all cost the same
              whether a batch fills or not. The institutes that thrive are not
              necessarily the ones with the best courses — they are the ones
              whose courses reach professionals at the moment of need. These
              are the four leaks in that pipeline, and they compound: weak
              distribution forces discounting, discounting erodes the margin
              that funds equipment and instructors, and the quality gap you
              never had becomes one you cannot afford to close.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {INSTITUTE_FRICTIONS.map((friction, i) => (
              <Reveal key={friction.title} delay={i * 0.06}>
                <article className="group h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-magenta/25 hover:bg-panel-hover">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg border border-neon-magenta/15 bg-neon-magenta/8 p-2.5 transition-colors group-hover:bg-neon-magenta/12">
                      <friction.Icon className="h-5 w-5 text-neon-magenta" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">{friction.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ash">{friction.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Centerpiece: demand-matched discovery ────────────────────────── */}
      <section
        aria-labelledby="demand-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              The core mechanism
            </p>
            <h2 id="demand-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              How a professional&apos;s skill gap becomes your enrolment —
              step by step.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              This is not a roadmap slide. Every stage below is live in the
              platform today, running on the real profiles of the seafarers
              and shore professionals already registered. When we say
              &ldquo;measured demand&rdquo;, this is the measurement.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {DEMAND_CHAIN.map((item, i) => (
              <Reveal key={item.step} delay={i * 0.07}>
                <article className="h-full rounded-xl border border-neon-cyan/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/30 hover:bg-panel-hover">
                  <p className="font-mono text-sm font-bold text-neon-cyan">{item.step}</p>
                  <h3 className="mt-3 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="rounded-[1.75rem] border border-neon-purple/15 bg-panel/70 p-6 sm:p-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-neon-purple">
                    <ClipboardList className="h-5 w-5" aria-hidden="true" />
                    Why &ldquo;picking the canonical course&rdquo; matters
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-ash">
                    When you list, you select your course from the platform&apos;s
                    canonical catalogue rather than typing a free-text title.
                    That one choice is what connects your offering to
                    everything the platform knows: which ranks the course
                    applies to, which shore positions value it, and which
                    skill-gap rules it closes. Free-text listings on generic
                    directories are findable by keyword. Canonical listings
                    here are findable by need.
                  </p>
                </div>
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-neon-purple">
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                    Why the approval gate protects your enrolment
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-ash">
                    Only approved institutes can publish offerings, and only
                    published offerings from approved institutes are attached
                    to career-intelligence recommendations. That discipline is
                    the reason professionals trust the recommendations they
                    see — and the reason an enquiry from this platform is a
                    prospective student, not a price-shopping bot. The gate
                    keeps the signal worth paying attention to.
                  </p>
                </div>
              </div>
              <div className="mt-8 rounded-xl border border-neon-cyan/15 bg-void p-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon-cyan">
                  Worked example · one chain, end to end
                </p>
                <p className="mt-3 text-sm leading-7 text-ash">
                  A second engineer sets{" "}
                  <span className="text-foreground">technical superintendent</span>{" "}
                  as a target role. The engine scores their readiness at 74% and
                  names two open gaps:{" "}
                  <span className="text-foreground">fleet budgeting</span> and{" "}
                  <span className="text-foreground">multi-vessel oversight</span>.
                  The gap rules map to a management course with a 90+ relevance
                  score. If your institute publishes that course, your offering —
                  your name, your fee, your next batch with its registration
                  deadline and remaining seats — is attached to the
                  recommendation the engineer reads that evening. Multiply that
                  one chain by every professional running the same analysis
                  across every role your courses serve, and you have the
                  difference between being listed and being demanded.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Directory discovery ──────────────────────────────────────────── */}
      <section aria-labelledby="discovery-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-green">
              The public directory
            </p>
            <h2 id="discovery-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Found by location, filtered by credibility, chosen for the
              course.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Alongside the skill-gap engine sits the{" "}
              <Link href="/institutes" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                public institutes directory
              </Link>{" "}
              — the discovery surface professionals use when they know they
              need training and are deciding where. Listing places your
              institute inside it with the structure professionals actually
              filter by.
            </p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {DISCOVERY_POINTS.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-green/10 bg-panel/70 p-6 transition-colors hover:border-neon-green/30 hover:bg-panel-hover">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon-green/15 bg-neon-green/8">
                    <point.Icon className="h-5 w-5 text-neon-green" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{point.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{point.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── By institute type ────────────────────────────────────────────── */}
      <section
        aria-labelledby="types-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              By institute type
            </p>
            <h2 id="types-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Different training businesses, different wins.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              A statutory refresher mill and a boutique management school do
              not have the same enrolment problem. Here is what the platform
              does for each kind of training organisation, stated separately
              because the value is genuinely different.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {INSTITUTE_TYPES.map((type, i) => (
              <Reveal key={type.name} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-cyan/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/30 hover:bg-panel-hover">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon-cyan/15 bg-neon-cyan/8">
                    <type.Icon className="h-5 w-5 text-neon-cyan" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{type.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{type.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MidPageCta />

      {/* ── What listing involves ────────────────────────────────────────── */}
      <section
        aria-labelledby="listing-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-amber">
              The effort, honestly stated
            </p>
            <h2 id="listing-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              From registration to published courses in an afternoon.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Institutes evaluating a new platform deserve the effort estimate
              before the benefit pitch. Here is the complete process — all of
              it — with nothing hidden behind a sales call.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {LISTING_STEPS.map((item, i) => (
              <Reveal key={item.step} delay={i * 0.07}>
                <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-amber/30 hover:bg-panel-hover">
                  <p className="font-mono text-sm font-bold text-neon-amber">{item.step}</p>
                  <h3 className="mt-3 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Beyond courses ───────────────────────────────────────────────── */}
      <section aria-labelledby="beyond-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">
              More than listings
            </p>
            <h2 id="beyond-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              The rest of what an institute account does.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Course discovery is the reason to list. It is not the only thing
              your account can do once you are here.
            </p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {BEYOND_COURSES.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-purple/10 bg-panel/70 p-6 transition-colors hover:border-neon-purple/30 hover:bg-panel-hover">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon-purple/15 bg-neon-purple/8">
                    <item.Icon className="h-5 w-5 text-neon-purple" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <p className="max-w-3xl text-sm leading-7 text-ash">
              Curious why a maritime careers platform runs an institute
              vertical at all?{" "}
              <Link href="/about" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                Read the mission
              </Link>{" "}
              — or browse the{" "}
              <Link href="/faq" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                platform FAQ
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="faq-heading"
        className="border-t border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-4xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              Before you decide
            </p>
            <h2 id="faq-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              The questions institute directors ask us.
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((item, i) => (
              <Reveal key={item.question} delay={i * 0.04}>
                <details className="group rounded-xl border border-white/10 bg-panel/70 p-5 transition-colors open:border-neon-cyan/25 hover:border-neon-cyan/20">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold [&::-webkit-details-marker]:hidden">
                    {item.question}
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-neon-cyan transition-transform group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="mt-3 text-sm leading-7 text-ash">{item.answer}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 sm:p-12">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
                <Landmark className="h-4 w-4" aria-hidden="true" />
                Fill the next batch, not just the brochure.
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">
                List once. Be found every time the engine names your gap.
              </h2>
              <p className="mt-4 text-base leading-7 text-ash">
                Register in one step, pass one review, pick your courses from
                the catalogue and schedule your batches. From then on, every
                professional whose career analysis identifies a gap your
                course closes sees your offering — with your name, your fee
                and your seats — at the exact moment they are ready to act on
                it. No listing fee, no per-lead charge, no intermediary
                between the enquiry and your admissions team. The demand is
                already being measured. The only question is whether your
                courses are standing in front of it.
              </p>
            </div>
            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-center">
              <PrimaryCta large />
              <SignInLink className="text-center" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
